import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, DestroyRef, OnInit, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, finalize } from 'rxjs';
import { OuiButtonModule } from 'omnium-ui/button';
import { OuiDialogModule, OuiDialogService } from 'omnium-ui/dialog';
import { OuiIconModule } from 'omnium-ui/icon';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { OuiTagModule } from 'omnium-ui/tag';
import { OuiTooltipModule } from 'omnium-ui/tooltip';
import { appRoutePaths } from '../../../../app.paths';
import { ActionAuthorizationService, doublonsBusterActionCodes } from '../../../../core/auth/action-authorization.service';
import { ConsultantCardComponent } from '../../../../core/components/consultant-card/consultant-card.component';
import {
  BouquetServiceConsultantRow,
  ConsultantViewerCompliance,
  ConsultantViewerContacts,
  ConsultantViewerOverview,
  ConsultantViewerOrganization,
} from '../../../../core/models/api.models';
import { DoublonsBusterApiService } from '../../../../core/services/doublons-buster-api.service';
import { ConsultantStatusTagComponent, getConsultantStatusLabel } from '../../shared/consultant-status-tag/consultant-status-tag.component';
import { SummaryGridComponent, SummaryGridItem } from '../../shared/summary-grid/summary-grid.component';
import { StateCardComponent } from '../../shared/state-card/state-card.component';
import { formatAddress, formatBoolean, formatDate, formatIdentity, formatPercentage, formatPhone, formatText } from './consultant-viewer-format.utils';
import { ConsultantSubscriptionTagComponent } from '../../shared/consultant-subscription-tag/consultant-subscription-tag.component';
import {
  BouquetServiceResiliationDialogComponent,
  BouquetServiceResiliationDialogResult,
} from '../bouquet-service/bouquet-service-resiliation-dialog/bouquet-service-resiliation-dialog.component';
import {
  BouquetServiceActivationDialogComponent,
  BouquetServiceActivationDialogResult,
} from '../bouquet-service/bouquet-service-activation-dialog/bouquet-service-activation-dialog.component';

@Component({
  selector: 'dbw-consultant-inspection-page',
  imports: [
    OuiButtonModule,
    OuiDialogModule,
    OuiIconModule,
    OuiTagModule,
    OuiTooltipModule,
    ConsultantCardComponent,
    ConsultantStatusTagComponent,
    ConsultantSubscriptionTagComponent,
    StateCardComponent,
    SummaryGridComponent,
  ],
  templateUrl: './consultant-inspection.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['../investor-inspection/investor-viewer-section.shared.scss', './consultant-inspection.component.scss'],
})
export class ConsultantInspectionPageComponent implements OnInit {
  readonly destroyRef = inject(DestroyRef);

  readonly consultantId = signal(0);
  readonly loading = signal(true);
  readonly error = signal<string | undefined>(undefined);
  readonly contactsLoading = signal(false);
  readonly contactsError = signal<string | null>(null);
  readonly organizationLoading = signal(false);
  readonly organizationError = signal<string | null>(null);
  readonly complianceLoading = signal(false);
  readonly complianceError = signal<string | null>(null);
  readonly bouquetServiceLoading = signal(false);
  readonly bouquetServiceError = signal<string | null>(null);
  readonly overview = signal<ConsultantViewerOverview | null>(null);
  readonly contacts = signal<ConsultantViewerContacts | null>(null);
  readonly organization = signal<ConsultantViewerOrganization | null>(null);
  readonly compliance = signal<ConsultantViewerCompliance | null>(null);
  readonly bouquetServiceSubscription = signal<BouquetServiceConsultantRow | null>(null);
  readonly actionInProgressConsultantId = signal<number | undefined>(undefined);
  readonly canManageSubscription = computed(() =>
    this.actionAuthorizationService.canExecute(doublonsBusterActionCodes.consultantBouquetServiceSubscriptionManage)
  );

  readonly directContactCount = computed(() => {
    const contacts = this.contacts();
    return contacts ? contacts.directEmails.length + contacts.directPhones.length + contacts.directAddresses.length : 0;
  });

  readonly activeLegalStatusCount = computed(() => this.compliance()?.legalStatuses.filter(status => status.isActive).length ?? 0);

  readonly activeHabilitationCount = computed(
    () => this.compliance()?.habilitations.filter(habilitation => !habilitation.endDate).length ?? 0
  );

  readonly summaryGridItems = computed<SummaryGridItem[]>(() => [
    {
      key: 'status',
      label: 'Statut consultant',
      value: getConsultantStatusLabel(this.overview()?.statusCode),
    },
    {
      key: 'persons',
      label: 'Personnes liées',
      value: this.overview()?.persons.length ?? 0,
    },
    {
      key: 'contacts',
      label: 'Coordonnées directes',
      value: this.contactsLoading() && !this.contacts() ? '...' : this.directContactCount(),
      meta: this.contactsError() ? 'Chargement en erreur' : '',
    },
    {
      key: 'bouquet-service',
      label: 'Bouquet de service',
      value:
        this.bouquetServiceLoading() && !this.bouquetServiceSubscription()
          ? '...'
          : this.getSubscriptionLabel(this.bouquetServiceSubscription()),
      meta: this.bouquetServiceError() ? 'Chargement en erreur' : '',
    },
    {
      key: 'legal-statuses',
      label: 'Statuts juridiques',
      value: this.complianceLoading() && !this.compliance() ? '...' : (this.compliance()?.legalStatuses.length ?? 0),
      meta: `${this.activeLegalStatusCount()} actif(s)`,
    },
    {
      key: 'habilitations',
      label: 'Habilitations',
      value: this.complianceLoading() && !this.compliance() ? '...' : (this.compliance()?.habilitations.length ?? 0),
      meta: `${this.activeHabilitationCount()} sans date de fin`,
    },
  ]);

  readonly summaryMessage = computed(() => {
    const overview = this.overview();

    if (!overview) {
      return '';
    }

    const parts = [
      `${overview.persons.length} personne(s) physique(s) liée(s)`,
      `${this.directContactCount()} coordonnée(s) directe(s)`,
      `${this.activeLegalStatusCount()} statut(s) juridique(s) actif(s)`,
      `${this.activeHabilitationCount()} habilitation(s) sans date de fin`,
    ];

    if (overview.statusCode?.trim()) {
      parts.unshift(`Statut ${overview.statusCode.trim()}`);
    }

    return parts.join(' • ');
  });

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly apiService: DoublonsBusterApiService,
    private readonly titleService: Title,
    private readonly actionAuthorizationService: ActionAuthorizationService,
    private readonly snackbarService: OuiSnackbarService,
    private readonly dialogService: OuiDialogService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(paramMap => {
      const consultantId = Number(paramMap.get('consultantId'));

      if (!Number.isFinite(consultantId) || consultantId <= 0) {
        this.error.set('Consultant invalide.');
        this.titleService.setTitle('Consultant invalide');
        this.loading.set(false);
        return;
      }

      this.consultantId.set(consultantId);
      this.updateDocumentTitle();
      this.loadInspection();
    });
  }

  reload(): void {
    this.loadInspection();
  }

  openViewerSearch(): void {
    this.router.navigateByUrl(appRoutePaths.atlas.rechercheConsultant);
  }

  protected readonly formatText = formatText;
  protected readonly formatDate = formatDate;
  protected readonly formatIdentity = formatIdentity;
  protected readonly formatPhone = formatPhone;
  protected readonly formatAddress = formatAddress;
  protected readonly formatPercentage = formatPercentage;
  protected readonly formatBoolean = formatBoolean;

  activateSubscription(row: BouquetServiceConsultantRow): void {
    if (this.actionInProgressConsultantId() !== undefined) {
      return;
    }

    this.dialogService
      .openDialog(
        BouquetServiceActivationDialogComponent,
        {
          consultantId: row.consultantId,
          consultantLabel: row.displayName || `Consultant #${row.consultantId}`,
          currentEndDate: row.subscriptionStatus === 'terminationScheduled' ? row.subscriptionEndDate : null,
        },
        undefined,
        '520px'
      )
      .afterClosed()
      .subscribe((result?: BouquetServiceActivationDialogResult) => {
        if (!result?.endDate) {
          return;
        }

        this.executeSubscriptionAction(
          row.consultantId,
          () => this.apiService.activateConsultantBouquetServiceSubscription(row.consultantId, result.endDate),
          'Abonnement Bouquet de service activé.'
        );
      });
  }

  terminateSubscription(row: BouquetServiceConsultantRow): void {
    if (this.actionInProgressConsultantId() !== undefined) {
      return;
    }

    this.dialogService
      .openDialog(
        BouquetServiceResiliationDialogComponent,
        {
          consultantId: row.consultantId,
          consultantLabel: row.displayName || `Consultant #${row.consultantId}`,
          currentEndDate: row.subscriptionStatus === 'terminationScheduled' ? row.subscriptionEndDate : null,
        },
        undefined,
        '520px'
      )
      .afterClosed()
      .subscribe((result?: BouquetServiceResiliationDialogResult) => {
        if (!result?.terminationDate) {
          return;
        }

        this.executeSubscriptionAction(
          row.consultantId,
          () => this.apiService.terminateConsultantBouquetServiceSubscription(row.consultantId, result.terminationDate),
          'Abonnement Bouquet de service résilié.'
        );
      });
  }

  getActivationTooltip(row: BouquetServiceConsultantRow): string {
    return row.subscriptionStatus === 'terminationScheduled' ? 'Annuler le désabonnement' : "Activer l'abonnement";
  }

  private loadInspection(): void {
    this.loading.set(true);
    this.error.set(undefined);
    this.contactsLoading.set(true);
    this.contactsError.set(null);
    this.organizationLoading.set(true);
    this.organizationError.set(null);
    this.complianceLoading.set(true);
    this.complianceError.set(null);
    this.bouquetServiceLoading.set(true);
    this.bouquetServiceError.set(null);
    this.overview.set(null);
    this.contacts.set(null);
    this.organization.set(null);
    this.compliance.set(null);
    this.bouquetServiceSubscription.set(null);

    this.apiService.getConsultantAtlasViewerOverview(this.consultantId()).subscribe({
      next: overview => {
        this.overview.set(overview);
        this.updateDocumentTitle(overview.displayName);
        this.loading.set(false);
      },
      error: error => {
        this.overview.set(null);
        this.contactsLoading.set(false);
        this.organizationLoading.set(false);
        this.complianceLoading.set(false);
        this.error.set(
          error?.status === 404
            ? 'Le consultant demandé est introuvable.'
            : (error?.error?.detail ?? "Impossible de charger l'inspection consultant.")
        );
        this.loading.set(false);
      },
    });

    this.apiService.getConsultantAtlasViewerContacts(this.consultantId()).subscribe({
      next: contacts => {
        this.contacts.set(contacts);
        this.contactsLoading.set(false);
      },
      error: error => {
        this.contacts.set(null);
        this.contactsError.set(error?.error?.detail ?? 'Impossible de charger les coordonnées consultant.');
        this.contactsLoading.set(false);
      },
    });

    this.apiService.getConsultantAtlasViewerOrganization(this.consultantId()).subscribe({
      next: organization => {
        this.organization.set(organization);
        this.organizationLoading.set(false);
      },
      error: error => {
        this.organization.set(null);
        this.organizationError.set(error?.error?.detail ?? "Impossible de charger l'organisation consultant.");
        this.organizationLoading.set(false);
      },
    });

    this.apiService.getConsultantAtlasViewerCompliance(this.consultantId()).subscribe({
      next: compliance => {
        this.compliance.set(compliance);
        this.complianceLoading.set(false);
      },
      error: error => {
        this.compliance.set(null);
        this.complianceError.set(error?.error?.detail ?? 'Impossible de charger le cadre réglementaire consultant.');
        this.complianceLoading.set(false);
      },
    });

    this.loadBouquetServiceSubscription();
  }

  private loadBouquetServiceSubscription(): void {
    this.bouquetServiceLoading.set(true);
    this.bouquetServiceError.set(null);

    this.apiService
      .getBouquetServiceConsultantsPage(0, 1, {
        consultantId: this.consultantId(),
      })
      .subscribe({
        next: page => {
          this.bouquetServiceSubscription.set(page.items[0] ?? null);
          this.bouquetServiceLoading.set(false);
        },
        error: error => {
          this.bouquetServiceSubscription.set(null);
          this.bouquetServiceError.set(error?.error?.detail ?? 'Impossible de charger le Bouquet de service.');
          this.bouquetServiceLoading.set(false);
        },
      });
  }

  private executeSubscriptionAction(
    consultantId: number,
    request: () => Observable<{ message?: string | null }>,
    successMessage: string
  ): void {
    if (this.actionInProgressConsultantId() !== undefined) {
      return;
    }

    this.actionInProgressConsultantId.set(consultantId);
    request()
      .pipe(finalize(() => this.actionInProgressConsultantId.set(undefined)))
      .subscribe({
        next: result => {
          this.snackbarService.open(result.message || successMessage, 'success');
          this.loadBouquetServiceSubscription();
        },
        error: error => {
          this.snackbarService.open(error?.error?.detail ?? "L'action sur l'abonnement a échoué.", 'error');
        },
      });
  }

  private getSubscriptionLabel(row: BouquetServiceConsultantRow | null): string {
    switch (row?.subscriptionStatus) {
      case 'subscribed':
        return 'Abonné';
      case 'terminationScheduled':
        return 'Désabonnement programmé';
      case 'notSubscribed':
        return 'Non abonné';
      default:
        return '-';
    }
  }

  private updateDocumentTitle(displayName?: string | null): void {
    const title = displayName?.trim() || `Consultant #${this.consultantId()}`;
    this.titleService.setTitle(title);
  }
}
